import { FC, useEffect, useRef, useState } from 'react'
import { useQuery, useQueryClient } from 'react-query'
import { ComparacaoProtocolo } from '../comparacao/ComparacaoProtocolo'
import { CondicaoCatalogo } from '../../formularios/core/condicao/catalogo'
import { ErroProtocolo, extrairErrosProtocolo } from '../core/errosProtocolo'
import { ModeloVersaoDetalhe } from '../core/_models'
import { compararVersoesModelo, publicarModelo } from '../core/_requests'
import { Button } from '../../../../components/Button/Button'
import { useModal } from '../../../../components/Modal/ModalContext'

type Props = {
  versao: ModeloVersaoDetalhe
  catalogo?: CondicaoCatalogo
  onPublicado?: () => void
}

/**
 * Publicação de uma versão do modelo da plataforma (T7a, área da Lauduz).
 *
 * <p>Compõe as mesmas peças do modal de aprovação, mas é <b>outra regra</b>: publicar não exige
 * CRM nem vínculo — a supervisão clínica é a aprovação do protocolo da empresa.</p>
 *
 * <p>Conteúdo do `showModal` (título e fechamento ficam com o `ModalContext`).</p>
 */
export const PublicarModeloModal: FC<Props> = ({ versao, catalogo, onPublicado }) => {
  const queryClient = useQueryClient()
  const { closeModal } = useModal()
  const [enviando, setEnviando] = useState(false)
  const [erro, setErro] = useState<ErroProtocolo | null>(null)
  const alertaRef = useRef<HTMLDivElement>(null)

  const { data: comparacao } = useQuery(
    ['protocolos-triagem', 'modelo-comparacao', versao.id, 'publicada'],
    () => compararVersoesModelo(versao.id),
    { retry: false }
  )

  useEffect(() => {
    if (erro) {
      alertaRef.current?.focus()
    }
  }, [erro])

  const confirmar = async () => {
    setEnviando(true)
    setErro(null)
    try {
      await publicarModelo(versao.id, versao.hash)
      await queryClient.invalidateQueries(['protocolos-triagem'])
      onPublicado?.()
      closeModal()
    } catch (excecao) {
      setErro(extrairErrosProtocolo(excecao))
    } finally {
      setEnviando(false)
    }
  }

  const conteudoAlterado = erro?.code === 'CONTEUDO_ALTERADO'

  return (
    <>
      <dl className='row'>
        <dt className='col-sm-4'>Versão</dt>
        <dd className='col-sm-8'>
          {versao.versao} — {versao.nome}
        </dd>
        <dt className='col-sm-4'>Hash que será publicado</dt>
        <dd className='col-sm-8'>
          <code>{versao.hash}</code>
        </dd>
      </dl>

      <ul className='mb-6'>
        <li>A versão publicada será substituída por esta.</li>
        <li>Novos rascunhos criados pelas empresas a partir do modelo usarão esta versão.</li>
        <li>Protocolos já criados pelas empresas não mudam.</li>
      </ul>

      <ComparacaoProtocolo
        comparacao={comparacao}
        catalogo={catalogo}
        titulo='Diferenças para a versão publicada'
      />

      {erro && (
        <div className='alert alert-danger mt-6' role='alert' tabIndex={-1} ref={alertaRef}>
          <p className='mb-2'>{erro.message}</p>
          {conteudoAlterado && (
            <Button
              className='btn-light-danger d-inline-flex'
              size='btn-sm'
              onClick={() => window.location.reload()}
            >
              Recarregar
            </Button>
          )}
        </div>
      )}

      <div className='d-flex justify-content-end gap-2 mt-6'>
        <Button className='btn-light' onClick={closeModal} disabled={enviando}>
          Cancelar
        </Button>
        <Button className='btn-primary' onClick={confirmar} disabled={enviando}>
          {enviando ? 'Publicando...' : 'Publicar versão'}
        </Button>
      </div>
    </>
  )
}
